/* src/components/pages/Carpetascli.jsx - Última Modificación: 09-01-2026 */
/* ---------------- carpetas subidas tributarias cliente ---------------- */

import React, { useState } from 'react';
import { TextField, Box, Button, Stack, Dialog, DialogTitle,
         DialogActions, DialogContent, FormControlLabel,
         Checkbox, CircularProgress, Typography, Divider } from '@mui/material';

import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { CustomToolbar } from "../../components/datagrid/CustomToolbar";
import DataGridSkeleton from "../../components/datagrid/DataGridSkeleton";

import { exportPdfAsync, downloadBlob } from "../utils/exportUtils";
import { normalizeRut } from "../utils/rutUtils";

import { RiFolderDownloadLine, RiShareBoxFill, RiFileSearchLine,
         RiFilePdf2Fill } from "react-icons/ri";

import { useAuth } from "../../context/AuthContext"; // token

import { useFeedback } from '../../context/FeedbackContext'; // Mensajes breves

/* Campos para exportar a Csv */
const CSV_FIELDS = [
  { key: 'id', label: 'Id' },
  { key: 'fecha', label: 'Fecha' },
  { key: 'rutcli', label: 'RUT' },
  { key: 'nombre', label: 'Razón Social' },
  { key: 'tipo', label: 'Tipo Carpeta' },
];

const Carpetascli = ({ modo }) => {
  const { user } = useAuth();

  const [dataCarpetas, setCarpetas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [generandoPdf, setGenerandoPdf] = useState(false);

  const [openCsvDialog, setOpenCsvDialog] = useState(false);

  /* Selecciona todos los campos para Csv */
  const [selectedCsvFields, setSelectedCsvFields] = useState(
    CSV_FIELDS.map(f => f.key)
  );

  /* Estados para paginación desde el servidor */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* Dialogo y confirmación */
  const { showFeedback } = useFeedback(); // Mensajes breves no bloqueantes

  /* Función para obtener los datos. recibe la página solicitada */
  const fetchCarpetas = async ({ page = 0, pageSize = 20 } = {}) => {

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario)
      const RutEnr = '';
      const Tipousr = 1; // cliente

      const response = await api.post('/subircarpeta/getcarpetas', {
        RutCli,
        RutEnr,
        Tipousr,
        page,
        pageSize,
      });

      if (response.data.success && response.data.rows) {
        setCarpetas(response.data.rows);  // Carga los datos al estado //
        setRowCount(response.data.total); // Total registros //

      } else {
        setCarpetas([]);
        setRowCount(0);

        showFeedback({
          message: 'No se encontraron registros para los parámetros ingresados',
          severity: 'warning',
        });
      }

    } catch (err) {
      setCarpetas([]);
      setRowCount(0);

      showFeedback({
        message: 'No fue posible obtener su solicitud, por favor intente más tarde',
        severity: 'error',
      });

    } finally {
      setLoading(false);
    }
  };

  /* exportar a pdf todos los datos de la tabla */
  const exportToPDF = async () => {
    exportPdfAsync({
      endpointBase: "/subircarpeta/pdf",
      payload: {
        RutCli: normalizeRut(user.usuario),
        RutEnr: '',
        Tipousr: 1, /* Cliente */
      },
      onStart: () => setGenerandoPdf(true),
      onFinish: () => setGenerandoPdf(false),
      onError: (msg) => {
        setGenerandoPdf(false);
        showFeedback({
          message: msg,
          severity: "error",
        });
      },
    });
  };

  /* Helpers de Selección de campos para exportar a Csv */
  const toggleCsvField = (fieldKey) => {
    setSelectedCsvFields(prev =>
      prev.includes(fieldKey)
        ? prev.filter(k => k !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  /* Configuración de MUI DataGrid */
  const columns = [
    {
      field: 'pdf',
      headerName: 'PDF',
      width: 80,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,

      renderCell: (params) => {
        const { id } = params.row;

        const handleOpenPDF = async () => {
          if (!id) return;

          try {
            const response = await api.post('/subircarpeta/getcarpetapdf',
              { id },
              { responseType: 'blob' }
            );

            const pdfBlob = new Blob([response.data], {
              type: 'application/pdf',
            });

            const url = window.URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');

            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);

          } catch (error) {
            showFeedback({
              message: 'No fue posible obtener el Pdf',
              severity: 'error',
            });
          }
        };

        return (
          <Button
            variant="contained"
            aria-label="Abrir PDF"
            onClick={handleOpenPDF}
            sx={{
              backgroundColor: (theme) => theme.custom.ui.primaryAction,
              color: (theme) => theme.custom.ui.primaryActionText,
              minWidth: 0,
              padding: 0,
              width: 32,
              height: 24,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: (theme) =>
                  theme.custom.ui.primaryActionHover,
              },
            }}
          >
            <RiFilePdf2Fill size={20} />
          </Button>
        );
      },
    },
    {
      field: 'id',
      headerName: 'Id Carpeta',
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "fecha",
      headerName: "Fecha Actualización",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "—";
        return new Date(params.value).toLocaleString("es-CL");
      },
    },
    {
      field: 'rutcli',
      headerName: "Rut Contribuyente",
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'nombre',
      headerName: "Razón Social",
      width: 400,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'tipo',
      headerName: "Tipo Carpeta",
      width: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const value = params.row.tipo; // <-- asegúrate que aquí está el número 1 o 2

        return value === 1
          ? "Personalizada"
          : value === 2
          ? "Créditos"
          : value;
      },
    },
    {
      field: 'spacer',
      headerName: '',
      width: 345,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,   // ❗ elimina los 3 puntos
      disableReorder: true,      // evita que se arrastre
      disableExport: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: () => null,    // ❗ evita que se vea algo raro
    }
  ];

  /* Llama a crear Csv */
  const handleExportCsv = async () => {
    try {
      const RutCli = normalizeRut(user.usuario);

      const response = await api.post('/subircarpeta/csv',
        {
          RutCli,
          fields: selectedCsvFields,
        },
        {
          responseType: 'blob',
        }
      );

      downloadBlob(response.data, "carpetas_cliente.csv");
      setOpenCsvDialog(false);

    } catch (error) {
      showFeedback({
        message: 'No fue posible exportar el CSV',
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ width: '100%', pt: 0, overflow: 'visible' }}>
      {/* Barra de Controles */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <Button
          variant="contained"
          startIcon={<RiFileSearchLine size={18} />}
          onClick={() => {
            setPage(0);
            setCarpetas([]);
            fetchCarpetas({ page: 0, pageSize });
          }}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,

            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            color: (theme) => theme.custom.ui.primaryActionText,

            '&:hover': {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          Buscar
        </Button>

        <Button
          variant="contained"
          startIcon={<RiFolderDownloadLine size={18} />}
          disabled={dataCarpetas.length === 0}
          onClick={exportToPDF}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,
            backgroundColor: (theme) => theme.custom.ui.exportActionBg,
            color: (theme) => theme.custom.ui.exportActionText,
            border: (theme) =>
              `1px solid ${theme.custom.ui.exportActionBorder}`,

            '&:hover': {
              backgroundColor: (theme) =>
                theme.custom.ui.exportActionBgHover,
            },
          }}
        >
          PDF
        </Button>

        <Button
          variant="contained"
          startIcon={<RiShareBoxFill size={18} />}
          disabled={dataCarpetas.length === 0}
          onClick={() => setOpenCsvDialog(true)}
          sx={{
            minWidth: 110,
            height: 40,
            backgroundColor: (theme) => theme.custom.ui.exportActionBg,
            color: (theme) => theme.custom.ui.exportActionText,
            border: (theme) =>
              `1px solid ${theme.custom.ui.exportActionBorder}`,

            '&:hover': {
              backgroundColor: (theme) =>
                theme.custom.ui.exportActionBgHover,
            },
          }}
        >
          CSV
        </Button>

      </Stack>

      {/* Configuración MIU DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 210px)', // ajusta según tu layout
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={dataCarpetas}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}

          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);

            fetchCarpetas({
              page: finalPage,
              pageSize: newPageSize,
            });
          }}

          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString('es-CL')} registros encontrados
      </Typography>

      <Dialog
        open={generandoPdf}
        aria-labelledby="generando-pdf-title"
        aria-describedby="generando-pdf-desc"

        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: (theme) => ({
              borderRadius: 3,
              textAlign: 'center',
              backgroundColor: theme.custom.ui.modalBg,
              boxShadow: theme.custom.ui.modalShadow,
            }),
          },
        }}
      >
        <DialogContent>
          <Stack spacing={2} alignItems="center" py={2}>
            <CircularProgress
              size={42}
              thickness={4}
              sx={{
                color: (theme) => theme.custom.ui.focus,
              }}
            />

            <Typography id="generando-pdf-title" variant="body2">
              Generando reporte
            </Typography>

            <Typography
              id="generando-pdf-desc"
              variant="caption"
              color="text.secondary"
            >
              Estamos trabajando en ello…
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>


      {/* Dialogo selección de campos para Csv desde el backend */}
      <Dialog
        open={openCsvDialog}
        onClose={() => setOpenCsvDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              mb: 0,
              color: (theme) => theme.custom.ui.brandAccent,
              fontSize: '1.4rem'
            }}>
            Exportar CSV
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box sx={{ mb: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
            Selecciona los campos que deseas incluir en el archivo.
          </Box>

          <Stack spacing={0.5}>
            {CSV_FIELDS.map(field => (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    checked={selectedCsvFields.includes(field.key)}
                    onChange={() => toggleCsvField(field.key)}
                  />
                }
                label={field.label}
              />
            ))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenCsvDialog(false)}
            sx={{
              backgroundColor: (theme) => theme.custom.ui.secondaryActionBg,
              color: (theme) => theme.custom.ui.secondaryActionText,
              '&:hover': {
                backgroundColor: (theme) => theme.custom.ui.secondaryActionBgHover,
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={selectedCsvFields.length === 0}
            onClick={handleExportCsv}
            sx={{
              backgroundColor: (theme) => theme.custom.ui.primaryAction,
              color: (theme) => theme.custom.ui.primaryActionText,
              '&:hover': {
                backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
              },
            }}
          >
            Exportar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Carpetascli;
